import {DeviceId, DeviceSlotId, DeviceSlotTemplateId, ProjectId, UserId} from '../ids';
import * as _ from 'lodash';
import {isDefined} from "@webmate-web-core/shared/utils/utils";
import {DevicePropertyNames, PlatformFamily} from "@webmate-web-core/shared/types/devices/device-properties";
import {
    DeviceSlotReservation,
    deviceSlotReservationFromBackendObject
} from '@webmate-web-core/shared/types/devices/device-slot-reservation';

const RegularPropertiesSet = new Set<string>([DevicePropertyNames.ProviderType, DevicePropertyNames.ProviderId, DevicePropertyNames.OpenSTFSerial, DevicePropertyNames.VCloudTemplateGroup,
    DevicePropertyNames.Language, DevicePropertyNames.IP, DevicePropertyNames.MachinePlatformType, DevicePropertyNames.MachinePoolId, DevicePropertyNames.StartUpScriptState, "default", 'EmptyRequirement']);

const anyRequirementKey = "AnyRequirement";
const allRequirementKey = "AllRequirement";

interface PropertyRequirement {
    name: string,
    predicate: any
}

export class DeviceSlot {
    constructor(public readonly id: DeviceSlotId,
                public name: string,
                public readonly projects: ProjectId[],
                public readonly propertyBag: any,
                public readonly availableForDeployment: boolean,
                public readonly slotIsBeingReleased: boolean,
                public readonly deviceRequirement: any,
                public readonly template: DeviceSlotTemplateId,
                public readonly usersWorkingWithSlot?: UserId[],
                public readonly userBlockingSlot?: UserId,
                public readonly projectBlockingSlot?: ProjectId,
                public deviceId?: DeviceId,
                public readonly reservations?: DeviceSlotReservation[],
                public readonly state?: string) {
    }

    isAutoRedeployEnabledForProject(projectId: ProjectId): boolean{
        if (this.propertyBag['properties_by_project'] !== undefined){
            if (this.propertyBag['properties_by_project'][projectId] !== undefined){
                if (this.propertyBag['properties_by_project'][projectId]['autoRedeploy'] !== undefined){
                    return this.propertyBag['properties_by_project'][projectId]['autoRedeploy'];
                }
            }
        }
        return false;
    }

    copyWithNewAutoRedeployEnabledForProject(newValue: boolean, inProject: ProjectId): DeviceSlot {
        let tmp = _.cloneDeep(this.propertyBag);
        if (tmp['properties_by_project'] === undefined) {
            tmp['properties_by_project'] = {};
        }
        if (tmp['properties_by_project'][inProject] === undefined) {
            tmp['properties_by_project'][inProject] = {};
        }
        tmp['properties_by_project'][inProject]['autoRedeploy'] = newValue;

        return new DeviceSlot(this.id, this.name, this.projects, tmp, this.availableForDeployment,
            this.slotIsBeingReleased, this.deviceRequirement, this.template, this.usersWorkingWithSlot, this.userBlockingSlot, this.projectBlockingSlot, this.deviceId, this.reservations, this.state);
    }

    copyWithNewSlotReleaseStatus(newValue: boolean): DeviceSlot {
        return new DeviceSlot(this.id, this.name, this.projects, this.propertyBag, this.availableForDeployment,
            newValue, this.deviceRequirement, this.template, this.usersWorkingWithSlot, this.userBlockingSlot, this.projectBlockingSlot, this.deviceId, this.reservations, this.state);
    }

    /** returns the Amount of Users currently "working" with that Slot. Note that deploying a Device does not count as working (aka the Backend filters out the Creation Ticket) */
    getAmountOfActiveUsersOnSlot(): number {
        return this.usersWorkingWithSlot ? this.usersWorkingWithSlot.length : 0;
    }

    /** returns all IDs of Users currently "working" with that Slot. Note that deploying a Device does not count as working (aka the Backend filters out the Creation Ticket) */
    getActiveUsersOnSlot(): UserId[] {
        return this.usersWorkingWithSlot ? this.usersWorkingWithSlot : [];
    }

    isPublicSlot(): boolean {
        return this.propertyBag["displaytype"] === "mobilepool";
    }

    isPrivateSlot(): boolean {
        return this.propertyBag["displaytype"] === "singlemobile";
    }

    isHiddenSlot(): boolean {
        return this.propertyBag["displaytype"] === "hidden";
    }

    isHeadlessSlot(): boolean {
        return this.deviceRequirement[DevicePropertyNames.ProviderType] === "headless";
    }

    isWindowsSlot(): boolean {
        return this.deviceRequirement[DevicePropertyNames.MachinePlatformType] === "WINDOWS";
    }

    isMacOSSlot(): boolean {
        return this.deviceRequirement[DevicePropertyNames.MachinePlatformType] === "MACOS";
    }

    /** the platform family this slot can serve, undefined if its requirements do not say (custom slots) */
    getPlatformFamilyHint(): PlatformFamily | undefined {
        if (this.isPublicSlot() || this.isPrivateSlot() || this.isPrivateMobile()) {
            return 'mobile';
        }
        if (this.isVCloudSlot() || this.isWindowsSlot() || this.isMacOSSlot()) {
            return 'desktop';
        }
        return undefined;
    }

    isVCloudSlot(): boolean {
        return this.propertyBag['displaytype'] === "desktop";
    }

    getDeviceTypeLabel(): string {
        if (this.isPublicSlot()) {
            return 'Public Mobile Device';
        }
        else if (this.isPrivateSlot()) {
            return 'Private Mobile Device';
        }
        else if (this.isVCloudSlot() && this.isWindowsSlot()) {
            return 'Windows Virtual Machine';
        }
        else if (this.isVCloudSlot() && this.isMacOSSlot()) {
            return 'macOS Virtual Machine';
        }
        else if (this.isVCloudSlot() && !this.isMacOSSlot() && !this.isWindowsSlot()) {
            return 'Virtual Machine';
        }
        else if (this.isHeadlessSlot()) {
            return 'Headless Device';
        }

        return 'No suitable devices';
    }

    isSingleTemplate(): boolean {
        return isDefined(this.deviceRequirement[DevicePropertyNames.VCloudTemplateId]);
    }

    isPrivateMobile(): boolean {
        return isDefined(this.deviceRequirement[DevicePropertyNames.OpenSTFSerial]);
    }

    private _isCustomSlot?: boolean = undefined;

    isCustomSlot(): boolean {
        if (this._isCustomSlot !== undefined) {
            return this._isCustomSlot;
        }
        this._isCustomSlot = !this.hasOnlyRegularRequirements(this.deviceRequirement);
        return this._isCustomSlot;
    }

    private containedPropertyRequirements(requirements: any): PropertyRequirement[] {
        if (typeof requirements === 'object') {
            if (!requirements['type']) {
                // if the DeviceRequirement is a simple object (i.e. without 'type' field),
                // it's an implicit AllRequirement that only contains PropertyRequirements
                // i.e. we won't have to recurse deeper here but just return all fields
                return <PropertyRequirement[]><unknown>Object.entries(requirements);
            } else {
                const requirementType = requirements['type'];
                if (requirementType === 'anyof' || requirementType === 'allof') {
                    // this is an AnyRequirement or an AllRequirement
                    const anyItems = requirements['items'];
                    return _.flatMap(anyItems, this.containedPropertyRequirements);
                } else {
                    // that's a PropertyRequirement
                    return requirements as PropertyRequirement[];
                }
            }
        } else if (Array.isArray(requirements)) {
            // We are in an AllRequirement and every item may be whatever requirement type.
            return _.flatMap(requirements, this.containedPropertyRequirements);
        } else {
            throw new Error('unkown device requirement ' + requirements);
        }
    }

    private hasOnlyRegularRequirements(requirements: object): boolean {
        const reqs = _.uniq(this.containedPropertyRequirements(requirements).map(req => req.name));
        return _.difference(reqs, Array.from(RegularPropertiesSet)).filter(r => !!r).length === 0;
   }
}


/* the front-end discards the Device stored within a DeviceSlot. This little of inefficiency allows us a cleanly divide Slot and Device,
 * making working with it much easier! When and how devices are updated is entirely under the control of the Device Service (even if we
 * have to make one or two additional queries) */
export function deviceSlotFromBackendObject(d: any): DeviceSlot {
    if (d["name"] === undefined) {
        // every Slot always has a Name in front-end
        d["name"] = "Unnamed Slot";
    }

    if (d["deviceRequirement"] === undefined) {
        d["deviceRequirement"] = {};
    }

    let reservations = d.reservations ? d.reservations.map((r: any) => deviceSlotReservationFromBackendObject(r)) : undefined;

    return new DeviceSlot(d.id, d.name, d["projects"], d.propertyBag, d.availableForDeployment,
        d.slotIsBeingReleased, d.deviceRequirement, d.template, d.usersWorkingWithSlot, d.userBlockingSlot, d.projectBlockingSlot, d.deviceId, reservations, d.state);
}

/** canonical sorting function for slots, note that it does ONLY use static information so that Slots will not jump around because Devices are deployed or something */
export function slotSortingFunction(a: DeviceSlot, b: DeviceSlot): number {
    let result = getSortingValueForSlotType(a) - getSortingValueForSlotType(b);

    if (result !== 0) {
        return result;
    } else {
        return slotNameSortingFunction(a, b);
    }
}

/** returns a higher number if the slot type should be shown further down the list **/
function getSortingValueForSlotType(slot: DeviceSlot): number {
    if (slot.isVCloudSlot()) {
        return 0;
    }
    if (slot.isWindowsSlot()) {
        return 10;
    }
    if (slot.isMacOSSlot()) {
        return 20;
    }
    if (slot.isSingleTemplate()) {
        return 30;
    }
    // we assume these are desktops for now
    if (slot.isCustomSlot()) {
        return 40;
    }
    if (slot.isPrivateMobile()) {
        return 50;
    }
    //public slots should be last
    if (!slot.isPublicSlot()) {
        return 60;
    }
    return 70;
}

function slotNameSortingFunction(a: DeviceSlot, b: DeviceSlot): number {
    if (a.name === b.name /* check the name next*/) {
        return a.id.localeCompare(b.id);
    } else {
        return a.name.localeCompare(b.name, undefined,{ numeric: true });
    }
}
